import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { logout, refreshToken, setUser, loginSuccess, initializationComplete, completeOnboarding, requireOnboarding } from '../redux/authSlice';
import axios from 'axios';
import { isDevAccount, getEffectiveTier } from '../services/devAccountMiddleware';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  email: string | null;
  tier: string | null;
  isDevAccount: boolean;
}

type AuthAction =
  | { type: 'LOGIN'; payload: { token: string; email: string } }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_TOKEN'; payload: string };

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  email: null,
  tier: null,
  isDevAccount: false
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN':
      const isDev = isDevAccount(action.payload.email);
      return {
        ...state,
        isAuthenticated: true,
        token: action.payload.token,
        email: action.payload.email,
        isDevAccount: isDev,
        // If it's a dev account, always use pro tier
        tier: isDev ? 'pro' : state.tier
      };
    case 'LOGOUT':
      return initialState;
    case 'REFRESH_TOKEN':
      return {
        ...state,
        token: action.payload
      };
    default:
      return state;
  }
};

interface AuthContextType {
  isAuthenticated: boolean;
  user: {
    email: string | null;
    tier: string | null;
    isDevAccount: boolean;
  } | null;
  login: (token: string, email: string, hasCompletedOnboarding?: boolean) => void;
  logout: () => void;
  refreshAuth: () => Promise<string>;
  completeOnboarding: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, dispatch] = useReducer(authReducer, initialState);
  const reduxDispatch = useDispatch();

  // Let AuthFlow handle initialization
  useEffect(() => {
    // AuthFlow component will handle the initialization
    // This effect is now minimal to avoid conflicts
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authState.token) {
          // Only verify if we have a token and it's been more than 5 minutes since last check
          const lastCheck = localStorage.getItem('lastAuthCheck');
          const now = Date.now();
          
          if (!lastCheck || now - parseInt(lastCheck) > 5 * 60 * 1000) {
            await axios.get('/api/auth/verify');
            localStorage.setItem('lastAuthCheck', now.toString());
          }
        }
      } catch (error) {
        console.warn('Auth verification failed:', error);
        // Don't logout immediately on verification failure - the token might still be valid
        // Only logout if it's a 401/403 error
        if ((error as any)?.response?.status === 401 || (error as any)?.response?.status === 403) {
          dispatch({ type: 'LOGOUT' });
          reduxDispatch(logout());
          localStorage.removeItem('token');
          localStorage.removeItem('email');
        }
      }
    };

    // Only check auth if we have a token
    if (authState.token) {
      checkAuth();
    }
  }, [authState.token, reduxDispatch]);

  const login = (token: string, email: string, hasCompletedOnboarding: boolean = false) => {
    dispatch({ type: 'LOGIN', payload: { token, email } });
    
    // Determine if user needs onboarding
    const isDev = isDevAccount(email);
    const needsOnboard = isDev || !hasCompletedOnboarding;
    
    reduxDispatch(loginSuccess({ 
      token, 
      email, 
      hasCompletedOnboarding: !needsOnboard,
      user: { email, tier: isDev ? 'pro' : 'free' }
    }));
    
    // For dev accounts, always require onboarding for testing
    if (isDev) {
      reduxDispatch(requireOnboarding());
    }
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    reduxDispatch(logout());
  };

  const refreshAuth = async () => {
    try {
      const response = await axios.post('/api/auth/refresh');
      const newToken = response.data.token;
      dispatch({ type: 'REFRESH_TOKEN', payload: newToken });
      reduxDispatch(refreshToken(newToken));
      return newToken;
    } catch (error) {
      dispatch({ type: 'LOGOUT' });
      reduxDispatch(logout());
      throw error;
    }
  };

  const handleCompleteOnboarding = () => {
    reduxDispatch(completeOnboarding());
  };

  const value: AuthContextType = {
    isAuthenticated: authState.isAuthenticated,
    user: authState.email ? { 
      email: authState.email, 
      tier: getEffectiveTier(authState.email, authState.tier),
      isDevAccount: authState.isDevAccount 
    } : null,
    login,
    logout: handleLogout,
    refreshAuth,
    completeOnboarding: handleCompleteOnboarding
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 